import type {Catalog, ClassData, FeatureData} from './catalog';
import {characterLevel, CharacterState, ClassLevel} from './character-state';
import {createClassBaseFeatures} from './class-features';
import {propsAtLevel} from './level-props';
import {createLanguagesFeature, LANGUAGES_FEATURE_ID} from './languages-feature';
import {createOriginFeature} from './origin-feature';
import type {ChooseGroup, FeatureProps} from './types';

export type FeatureOwner = {
	/** область, в которой ищутся выборы по ссылкам вида `{ability}` */
	key: string;
	/** класс, по таблице которого считаются формулы */
	classId?: string;
	/** уровень для формул и props.levels: уровень класса, а у вида, происхождения и черт — персонажа */
	level: number;
};

export type ChoiceInstance = {
	/** ключ, под которым выбор хранится в состоянии */
	key: string;
	/** ключ экземпляра особенности, которой принадлежит выбор */
	featureKey: string;
	group: ChooseGroup;
	owner: FeatureOwner;
	/** уровень, на котором сделан выбор */
	level: number;
	/** сохранённый выбор, иначе предустановленный или по умолчанию */
	selected: string[];
	/** значение задано снаружи (вариант черты у происхождения) и не меняется */
	locked: boolean;
	/** выборы, которые открыли выбранные варианты */
	nested: ChoiceInstance[];
};

export type FeatureInstance = {
	key: string;
	feature: FeatureData;
	owner: FeatureOwner;
	/** уровень, на котором особенность получена */
	level: number;
	/** props на уровне владельца; у повторов особенности на следующих уровнях не задаются */
	props?: FeatureProps;
	choices: ChoiceInstance[];
	/** черты и особенности, полученные выборами этой особенности */
	children: FeatureInstance[];
};

export type ClassFeatures = {
	classLevel: ClassLevel;
	classData: ClassData;
	isStartingClass: boolean;
	subClass?: {id: string; title: string};
	features: FeatureInstance[];
};

export type CharacterFeatures = {
	classes: ClassFeatures[];
	specie: FeatureInstance[];
	origin: FeatureInstance[];
};

type Presets = Record<string, string>;

type ChoicePlacement = {
	key: string;
	featureKey: string;
	owner: FeatureOwner;
	level: number;
	presets?: Presets;
};

export const buildCharacterFeatures = (state: CharacterState, catalog: Catalog): CharacterFeatures =>
	new FeatureTreeBuilder(state, catalog).build();

class FeatureTreeBuilder {
	private readonly characterLevel: number;

	constructor(private readonly state: CharacterState, private readonly catalog: Catalog) {
		this.characterLevel = characterLevel(state.classes);
	}

	build(): CharacterFeatures {
		return {
			classes: this.state.classes.flatMap((classLevel, index) => this.buildClass(classLevel, index === 0)),
			specie: this.buildSpecie(),
			origin: this.buildOrigin(),
		};
	}

	private buildClass(classLevel: ClassLevel, isStartingClass: boolean): ClassFeatures[] {
		const classData = this.catalog.getClass(classLevel.classId);
		if (!classData) return [];

		const owner: FeatureOwner = {key: `class:${classData.id}`, classId: classData.id, level: classLevel.level};
		const classFeatures = [
			...createClassBaseFeatures(classData, isStartingClass),
			...this.catalog.classFeatures(classData.id),
		].flatMap((feature) => this.instantiateByLevels(feature, owner));

		const subClassId = findSubClassId(classFeatures);
		const subClassFeatures = subClassId
			? this.catalog.subClassFeatures(subClassId).flatMap((feature) => this.instantiateByLevels(feature, owner))
			: [];

		return [
			{
				classLevel,
				classData,
				isStartingClass,
				subClass: subClassId
					? {id: subClassId, title: this.catalog.subClassTitle(subClassId) || subClassId}
					: undefined,
				features: [...classFeatures, ...subClassFeatures].sort(compareFeatures),
			},
		];
	}

	private buildSpecie(): FeatureInstance[] {
		const {specieId} = this.state;
		if (!specieId) return [];
		const owner: FeatureOwner = {key: 'specie', level: this.characterLevel};
		return this.catalog
			.specieFeatures(specieId)
			.slice()
			.sort(compareByOrder)
			.map((feature) => this.createFeature(feature, `${owner.key}/${feature.id}`, 1, owner));
	}

	private buildOrigin(): FeatureInstance[] {
		const origin = this.state.originId ? this.catalog.getOrigin(this.state.originId) : undefined;
		if (!origin) return [];
		const owner: FeatureOwner = {key: 'origin', level: this.characterLevel};
		const feature = createOriginFeature(origin);
		return [
			this.createFeature(feature, `${owner.key}/${origin.id}`, 1, owner),
			this.createFeature(createLanguagesFeature(), `${owner.key}/${LANGUAGES_FEATURE_ID}`, 1, owner),
		];
	}

	private instantiateByLevels(feature: FeatureData, owner: FeatureOwner): FeatureInstance[] {
		const reachedLevels = (feature.levels.length ? feature.levels : [1]).filter((level) => level <= owner.level);
		if (!reachedLevels.length) return [];

		const [firstLevel] = reachedLevels;
		if (!repeatsChoiceEachLevel(feature)) {
			return [this.createFeature(feature, `${owner.key}/${feature.id}`, firstLevel, owner)];
		}
		return reachedLevels.map((level) =>
			this.createFeature(feature, `${owner.key}/${feature.id}@${level}`, level, owner, {
				withProps: level === firstLevel,
			}),
		);
	}

	private createFeature(
		feature: FeatureData,
		key: string,
		level: number,
		owner: FeatureOwner,
		{withProps = true, presets}: {withProps?: boolean; presets?: Presets} = {},
	): FeatureInstance {
		const choices = feature.choose.map((group, index) =>
			this.createChoice(group, {key: `${key}#${index}`, featureKey: key, owner, level, presets}),
		);
		return {
			key,
			feature,
			owner,
			level,
			props: withProps ? propsAtLevel(feature.props, owner.level) : undefined,
			choices,
			children: choices.flatMap((choice) => this.grantedFeatures(choice)),
		};
	}

	private createChoice(group: ChooseGroup, placement: ChoicePlacement): ChoiceInstance {
		const {key, featureKey, owner, level, presets} = placement;
		const preset = presets?.[group.key];
		const stored = this.state.choices[key];
		const selected = preset ? [preset] : stored?.length ? stored : group.default ? [group.default] : [];
		const nested = (group.options || [])
			.filter((option) => selected.includes(option.key))
			.flatMap((option) =>
				(option.choose || []).map((nestedGroup, index) =>
					this.createChoice(nestedGroup, {...placement, key: `${key}>${option.key}#${index}`}),
				),
			);
		return {key, featureKey, group, owner, level, selected, locked: !!preset, nested};
	}

	private grantedFeatures(choice: ChoiceInstance): FeatureInstance[] {
		const nestedFeatures = choice.nested.flatMap((nestedChoice) => this.grantedFeatures(nestedChoice));
		if (choice.group.type === 'feat') {
			return [...choice.selected.flatMap((featId) => this.featFeatures(featId, choice)), ...nestedFeatures];
		}
		if (choice.group.type === 'feature') {
			const chosenFeatures = choice.selected
				.map((featureId) => this.catalog.getFeature(featureId))
				.filter((feature): feature is FeatureData => !!feature)
				.map((feature) =>
					this.createFeature(feature, `${choice.key}>${feature.id}`, choice.level, choice.owner),
				);
			return [...chosenFeatures, ...nestedFeatures];
		}
		return nestedFeatures;
	}

	private featFeatures(featId: string, choice: ChoiceInstance): FeatureInstance[] {
		const feat = this.catalog.getFeat(featId);
		const owner: FeatureOwner = {key: `${choice.key}>${featId}`, level: this.characterLevel};
		const {variant} = choice.group.params;
		const presets = feat?.variantKey && variant ? {[feat.variantKey]: variant} : undefined;
		return this.catalog
			.featFeatures(featId)
			.slice()
			.sort(compareByOrder)
			.map((feature) =>
				this.createFeature(feature, `${owner.key}/${feature.id}`, choice.level, owner, {presets}),
			);
	}
}

const repeatsChoiceEachLevel = (feature: FeatureData) =>
	feature.levels.length > 1 &&
	feature.choose.length > 0 &&
	!feature.choose.some((group) => group.type === 'subClass');

const findSubClassId = (features: FeatureInstance[]) =>
	features
		.flatMap((instance) => instance.choices)
		.find((choice) => choice.group.type === 'subClass')
		?.selected.at(0);

const compareByOrder = (first: FeatureData, second: FeatureData) => first.order - second.order;

const compareFeatures = (first: FeatureInstance, second: FeatureInstance) =>
	first.level - second.level ||
	Number(!!first.feature.subClassId) - Number(!!second.feature.subClassId) ||
	compareByOrder(first.feature, second.feature);

export const allFeatureInstances = (features: CharacterFeatures): FeatureInstance[] =>
	flattenFeatures([
		...features.classes.flatMap((classFeatures) => classFeatures.features),
		...features.specie,
		...features.origin,
	]);

export const flattenFeatures = (instances: FeatureInstance[]): FeatureInstance[] =>
	instances.flatMap((instance) => [instance, ...flattenFeatures(instance.children)]);

export const flattenChoices = (choices: ChoiceInstance[]): ChoiceInstance[] =>
	choices.flatMap((choice) => [choice, ...flattenChoices(choice.nested)]);
